import { useMemo, useState } from 'react'
import { Accordion, ActionIcon, Badge, Box, Button, Checkbox, Group, Select, Stack, Text, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { LifecycleChecklistItemDto, ProjectLifecycleStage } from '@ledokol/contracts'
import { useAuthStore } from '../auth/store'
import { fetchDepartments } from '../../shared/api/departments'
import {
  createChecklistItem, deleteChecklistItem, fetchLifecycleChecklist, toggleChecklistItem,
} from '../../shared/api/lifecycle-checklist'

const STAGES: { value: ProjectLifecycleStage; label: string }[] = [
  { value: 'lead', label: 'Лид' },
  { value: 'proposal', label: 'МП' },
  { value: 'approval', label: 'Согласование' },
  { value: 'contract', label: 'Договор' },
  { value: 'active_flight', label: 'Флайт' },
  { value: 'post_flight', label: 'Пост-флайт' },
  { value: 'repeat_request', label: 'Повторно' },
  { value: 'lost', label: 'Потерян' },
]

/** #10 — чеклисты под стадиями: секции стадий → группы отделов (collapse) → пункты. */
export function LifecycleChecklist({ projectId }: { projectId: string }) {
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const canManage = user?.permissions.includes('checklist.manage') ?? false
  const canToggle = user?.permissions.includes('checklist.toggle') ?? false

  const { data: items } = useQuery({
    queryKey: ['lifecycle-checklist', projectId],
    queryFn: () => fetchLifecycleChecklist(projectId),
  })
  const { data: depts } = useQuery({ queryKey: ['departments'], queryFn: fetchDepartments })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['lifecycle-checklist', projectId] })
  const toggleMut = useMutation({
    mutationFn: (v: { id: string; done: boolean }) => toggleChecklistItem(projectId, v.id, v.done),
    onSuccess: invalidate,
    onError: (e: unknown) => notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Ошибка' }),
  })
  const delMut = useMutation({
    mutationFn: (id: string) => deleteChecklistItem(projectId, id),
    onSuccess: invalidate,
  })

  const byStage = useMemo(() => {
    const m = new Map<string, LifecycleChecklistItemDto[]>()
    for (const it of items ?? []) {
      if (!m.has(it.stage)) m.set(it.stage, [])
      m.get(it.stage)!.push(it)
    }
    return m
  }, [items])

  return (
    <Box>
      <Text size="sm" fw={600} mb="xs">Чеклисты по стадиям</Text>
      <Accordion variant="separated" multiple>
        {STAGES.map((s) => {
          const stageItems = byStage.get(s.value) ?? []
          const done = stageItems.filter((i) => i.is_done).length
          return (
            <Accordion.Item key={s.value} value={s.value}>
              <Accordion.Control>
                <Group gap="xs">
                  <Text fw={600}>{s.label}</Text>
                  {stageItems.length > 0 && (
                    <Badge variant="light" color={done === stageItems.length ? 'green' : 'gray'}>
                      {done}/{stageItems.length}
                    </Badge>
                  )}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <StagePanel
                  projectId={projectId}
                  stage={s.value}
                  items={stageItems}
                  depts={(depts ?? []).map((d) => ({ value: d.id, label: d.name }))}
                  canManage={canManage}
                  canToggle={canToggle}
                  onToggle={(id, dn) => toggleMut.mutate({ id, done: dn })}
                  onDelete={(id) => delMut.mutate(id)}
                  onCreated={invalidate}
                />
              </Accordion.Panel>
            </Accordion.Item>
          )
        })}
      </Accordion>
    </Box>
  )
}

function StagePanel({
  projectId, stage, items, depts, canManage, canToggle, onToggle, onDelete, onCreated,
}: {
  projectId: string
  stage: ProjectLifecycleStage
  items: LifecycleChecklistItemDto[]
  depts: { value: string; label: string }[]
  canManage: boolean
  canToggle: boolean
  onToggle: (id: string, done: boolean) => void
  onDelete: (id: string) => void
  onCreated: () => void
}) {
  // Группировка пунктов по отделу (collapse — вложенный Accordion, по умолчанию раскрыт).
  const groups = useMemo(() => {
    const m = new Map<string, { name: string; items: LifecycleChecklistItemDto[] }>()
    for (const it of items) {
      if (!m.has(it.department.id)) m.set(it.department.id, { name: it.department.name, items: [] })
      m.get(it.department.id)!.items.push(it)
    }
    return [...m.entries()]
  }, [items])

  const [deptId, setDeptId] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [adding, setAdding] = useState(false)

  async function add() {
    if (!deptId || !text.trim()) return
    setAdding(true)
    try {
      await createChecklistItem(projectId, { stage, department_id: deptId, text: text.trim() })
      setText('')
      onCreated()
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Ошибка' })
    } finally {
      setAdding(false)
    }
  }

  return (
    <Stack gap="sm">
      {items.length === 0 && <Text size="sm" c="dimmed">Пунктов пока нет.</Text>}
      {groups.length > 0 && (
        <Accordion variant="contained" multiple defaultValue={groups.map(([id]) => id)}>
          {groups.map(([id, g]) => (
            <Accordion.Item key={id} value={id}>
              <Accordion.Control>
                <Text size="sm" fw={500}>{g.name} ({g.items.filter((i) => i.is_done).length}/{g.items.length})</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap={6}>
                  {g.items.map((it) => (
                    <Group key={it.id} gap="xs" wrap="nowrap" justify="space-between">
                      <Checkbox
                        checked={it.is_done}
                        disabled={!canToggle}
                        onChange={(e) => onToggle(it.id, e.currentTarget.checked)}
                        label={<Text size="sm" td={it.is_done ? 'line-through' : undefined} c={it.is_done ? 'dimmed' : undefined}>{it.text}</Text>}
                      />
                      <Group gap={6} wrap="nowrap">
                        {it.is_done && it.done_by && <Text size="xs" c="dimmed">{it.done_by.full_name}</Text>}
                        {canManage && (
                          <ActionIcon variant="subtle" color="red" size="sm" onClick={() => onDelete(it.id)}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Group>
                  ))}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
      {canManage && (
        <Group gap="xs" align="flex-end">
          <Select placeholder="Отдел" data={depts} value={deptId} onChange={setDeptId} w={180} searchable />
          <TextInput placeholder="Новый пункт" value={text} onChange={(e) => setText(e.currentTarget.value)} style={{ flex: 1 }} />
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={add} loading={adding} disabled={!deptId || !text.trim()}>
            Добавить
          </Button>
        </Group>
      )}
    </Stack>
  )
}
